import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  cancelCampaign,
  convertLead,
  createCampaign,
  createCampaignTemplate,
  createInterest,
  createLead,
  createReferral,
  createSegment,
  createSequence,
  deleteCampaign,
  deleteCampaignTemplate,
  deleteInterest,
  deleteLead,
  deleteReferral,
  deleteSegment,
  deleteSequence,
  enrollCustomer,
  fetchAttribution,
  fetchAudience,
  fetchCampaignById,
  fetchCampaignRecipients,
  fetchCampaigns,
  fetchCampaignTemplates,
  fetchInterests,
  fetchLeads,
  fetchMarketingOverview,
  fetchReferrals,
  fetchSequenceEnrollments,
  fetchSequences,
  fetchSegments,
  previewSegment,
  scheduleCampaign,
  sendCampaign,
  setConsent,
  updateCampaignTemplate,
  updateLead,
  updateReferral,
  updateSegment,
  updateSequence,
} from './api'
import type {
  CampaignChannel,
  LeadWriteBody,
  ReferralWriteBody,
  SegmentRules,
  SequenceWriteBody,
} from '@/types/marketing'

export const SEGMENTS_KEY = ['segments'] as const
export const CAMPAIGN_TEMPLATES_KEY = ['marketing-templates'] as const
export const CAMPAIGNS_KEY = ['campaigns'] as const
export const AUDIENCE_KEY = ['marketing-audience'] as const

// Marketing writes are plain online mutations (no offline outbox): sends and
// schedules fire real deliveries, and creates have no client-id replay
// dedupe on the server — queueing either for automatic replay while offline
// could surprise the operator more than an error toast would.

export const useSegments = () => useQuery({ queryKey: SEGMENTS_KEY, queryFn: fetchSegments })

export const useCreateSegment = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createSegment,
    onSuccess: () => qc.invalidateQueries({ queryKey: SEGMENTS_KEY }),
  })
}

export const useUpdateSegment = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof updateSegment>[1] }) =>
      updateSegment(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: SEGMENTS_KEY }),
  })
}

export const useDeleteSegment = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteSegment,
    onSuccess: () => qc.invalidateQueries({ queryKey: SEGMENTS_KEY }),
  })
}

export const useSegmentPreview = () =>
  useMutation({ mutationFn: (rules: SegmentRules) => previewSegment(rules) })

export const useCampaignTemplates = () =>
  useQuery({ queryKey: CAMPAIGN_TEMPLATES_KEY, queryFn: fetchCampaignTemplates })

export const useCreateCampaignTemplate = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createCampaignTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: CAMPAIGN_TEMPLATES_KEY }),
  })
}

export const useUpdateCampaignTemplate = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string
      body: Parameters<typeof updateCampaignTemplate>[1]
    }) => updateCampaignTemplate(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: CAMPAIGN_TEMPLATES_KEY }),
  })
}

export const useDeleteCampaignTemplate = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteCampaignTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: CAMPAIGN_TEMPLATES_KEY }),
  })
}

export const useCampaigns = () => useQuery({ queryKey: CAMPAIGNS_KEY, queryFn: fetchCampaigns })

export const useCampaign = (id: string) =>
  useQuery({
    queryKey: [...CAMPAIGNS_KEY, id],
    queryFn: () => fetchCampaignById(id),
    enabled: !!id,
  })

export const useCampaignRecipients = (id: string) =>
  useQuery({
    queryKey: [...CAMPAIGNS_KEY, id, 'recipients'],
    queryFn: () => fetchCampaignRecipients(id),
    enabled: !!id,
  })

const invalidateCampaign = (qc: ReturnType<typeof useQueryClient>, id?: string) => {
  qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY })
  if (id) qc.invalidateQueries({ queryKey: [...CAMPAIGNS_KEY, id] })
  qc.invalidateQueries({ queryKey: [...CAMPAIGNS_KEY, id, 'recipients'] })
}

export const useCreateCampaign = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createCampaign,
    onSuccess: () => qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY }),
  })
}

export const useSendCampaign = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: sendCampaign,
    onSuccess: (c) => invalidateCampaign(qc, c.id),
  })
}

export const useScheduleCampaign = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, scheduled_at }: { id: string; scheduled_at: string }) =>
      scheduleCampaign(id, scheduled_at),
    onSuccess: (c) => invalidateCampaign(qc, c.id),
  })
}

export const useCancelCampaign = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: cancelCampaign,
    onSuccess: (c) => invalidateCampaign(qc, c.id),
  })
}

export const useDeleteCampaign = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteCampaign,
    onSuccess: () => qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY }),
  })
}

export const useAudience = () => useQuery({ queryKey: AUDIENCE_KEY, queryFn: fetchAudience })

export const useSetConsent = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      customerId,
      channel,
      opt_in,
    }: {
      customerId: string
      channel: CampaignChannel
      opt_in: boolean
    }) => setConsent(customerId, channel, opt_in),
    onSuccess: () => qc.invalidateQueries({ queryKey: AUDIENCE_KEY }),
  })
}

// --- Interests (inventory alerts) ---

export const INTERESTS_KEY = ['marketing-interests'] as const

export const useInterests = () => useQuery({ queryKey: INTERESTS_KEY, queryFn: fetchInterests })

export const useCreateInterest = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createInterest,
    onSuccess: () => qc.invalidateQueries({ queryKey: INTERESTS_KEY }),
  })
}

export const useDeleteInterest = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteInterest,
    onSuccess: () => qc.invalidateQueries({ queryKey: INTERESTS_KEY }),
  })
}

// --- Nurture sequences ---

export const SEQUENCES_KEY = ['sequences'] as const

export const useSequences = () => useQuery({ queryKey: SEQUENCES_KEY, queryFn: fetchSequences })

export const useSequenceEnrollments = (id: string) =>
  useQuery({
    queryKey: [...SEQUENCES_KEY, id, 'enrollments'],
    queryFn: () => fetchSequenceEnrollments(id),
    enabled: !!id,
  })

export const useCreateSequence = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createSequence,
    onSuccess: () => qc.invalidateQueries({ queryKey: SEQUENCES_KEY }),
  })
}

export const useUpdateSequence = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SequenceWriteBody }) => updateSequence(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: SEQUENCES_KEY }),
  })
}

export const useDeleteSequence = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteSequence,
    onSuccess: () => qc.invalidateQueries({ queryKey: SEQUENCES_KEY }),
  })
}

export const useEnrollCustomer = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ sequenceId, customerId }: { sequenceId: string; customerId: string }) =>
      enrollCustomer(sequenceId, customerId),
    onSuccess: () => qc.invalidateQueries({ queryKey: SEQUENCES_KEY }),
  })
}

// --- Overview ---

export const MARKETING_OVERVIEW_KEY = ['marketing-overview'] as const

export const useMarketingOverview = (months = 12) =>
  useQuery({
    queryKey: [...MARKETING_OVERVIEW_KEY, months],
    queryFn: () => fetchMarketingOverview(months),
  })

// --- Leads (prospect intake) ---

export const LEADS_KEY = ['marketing-leads'] as const

export const useLeads = () => useQuery({ queryKey: LEADS_KEY, queryFn: fetchLeads })

export const useCreateLead = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createLead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LEADS_KEY })
      qc.invalidateQueries({ queryKey: ATTRIBUTION_KEY })
    },
  })
}

export const useUpdateLead = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: LeadWriteBody }) => updateLead(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LEADS_KEY })
      qc.invalidateQueries({ queryKey: ATTRIBUTION_KEY })
    },
  })
}

export const useConvertLead = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: convertLead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LEADS_KEY })
      qc.invalidateQueries({ queryKey: ATTRIBUTION_KEY })
      qc.invalidateQueries({ queryKey: REFERRALS_KEY })
      // Conversion creates a customer — refresh anything keyed on customers.
      qc.invalidateQueries({ queryKey: ['customers'] })
    },
  })
}

export const useDeleteLead = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteLead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LEADS_KEY })
      qc.invalidateQueries({ queryKey: ATTRIBUTION_KEY })
    },
  })
}

// --- Referrals ---

export const REFERRALS_KEY = ['marketing-referrals'] as const

export const useReferrals = () => useQuery({ queryKey: REFERRALS_KEY, queryFn: fetchReferrals })

export const useCreateReferral = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createReferral,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: REFERRALS_KEY })
      qc.invalidateQueries({ queryKey: ATTRIBUTION_KEY })
    },
  })
}

export const useUpdateReferral = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ReferralWriteBody }) =>
      updateReferral(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: REFERRALS_KEY })
      qc.invalidateQueries({ queryKey: ATTRIBUTION_KEY })
    },
  })
}

export const useDeleteReferral = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteReferral,
    onSuccess: () => qc.invalidateQueries({ queryKey: REFERRALS_KEY }),
  })
}

// --- Lead-source attribution ---

export const ATTRIBUTION_KEY = ['marketing-attribution'] as const

export const useAttribution = (months = 12) =>
  useQuery({
    queryKey: [...ATTRIBUTION_KEY, months],
    queryFn: () => fetchAttribution(months),
  })
